import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, TextField, Button, Paper, Alert, Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const LoginPage = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Generic message for any auth failure. Don't leak whether the username
  // exists, whether the password matched, or how close they are to a lockout.
  // Server-side rate limiting + account lockout are the actual defences.
  const GENERIC_ERROR = 'Invalid credentials.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(username, password);
      setLoading(false);

      if (result.requires_2fa) {
        navigate('/verify', {
          state: {
            otp_session_id: result.otp_session_id,
            masked_phone: result.masked_phone,
            masked_email: result.masked_email,
          },
        });
        return;
      }

      if (result.success) {
        switch (result.role) {
          case 'admin':
            navigate('/dashboard'); break;
          case 'client':
            navigate('/client-dashboard'); break;
          case 'nurse':
            navigate('/nurse-dashboard'); break;
          case 'carer':
            navigate('/carer-dashboard'); break;
          default:
            setError(GENERIC_ERROR); break;
        }
      } else {
        setError(GENERIC_ERROR);
      }
    } catch (err) {
      setLoading(false);
      // Hide all server errors behind the same generic message.
      // Only exception: network errors (no response at all).
      if (!err?.response) {
        setError('Network error. Please check your connection.');
      } else {
        setError(GENERIC_ERROR);
      }
    }
  };

  return (
    <Container maxWidth="xs">
      <Paper sx={{ p: 3, mt: 8 }}>
        <Typography variant="h5" gutterBottom>Login</Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Button size="small" onClick={() => navigate('/')}>
              ← Back to home
            </Button>
            <Button size="small" onClick={() => navigate('/forgot-password')}>
              Forgot Password?
            </Button>
          </Box>
          <Box textAlign="center" mt={2}>
            <Typography variant="body2">
              Don&apos;t have an account?{' '}
              <Button size="small" onClick={() => navigate('/register')} sx={{ textTransform: 'none' }}>
                Register
              </Button>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
